import type { Suggestion } from '../types/firestore';
import { getSuggestionBucket } from './suggestionBuckets';
import {
  normalizeSuggestionStatus,
  normalizeSuggestionWorkflowVersion,
} from './suggestionWorkflow';

const toMillis = (value: unknown): number => {
  if (!value) {
    return 0;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const timestamp = value as {
    toDate?: () => Date;
  };
  if (typeof timestamp.toDate === 'function') {
    try {
      return timestamp.toDate().getTime();
    } catch {
      return 0;
    }
  }

  return 0;
};

const isVotingOpen = (suggestion: Suggestion): boolean =>
  normalizeSuggestionWorkflowVersion(suggestion.workflowVersion) === 2 &&
  normalizeSuggestionStatus(suggestion.status) === 'voting_open' &&
  getSuggestionBucket(suggestion) !== 'history';

const getOperationalPriority = (suggestion: Suggestion): number => {
  if (isVotingOpen(suggestion)) {
    return 0;
  }

  const bucket = getSuggestionBucket(suggestion);
  switch (bucket) {
    case 'pre_vote':
      return 1;
    case 'post_vote':
      return 2;
    case 'implementation':
      return 3;
    case 'history':
      return 5;
    case 'voting':
      return 0;
    case 'all':
    default:
      return 4;
  }
};

const getPrimarySortMillis = (suggestion: Suggestion): number => {
  if (isVotingOpen(suggestion)) {
    return (
      toMillis(suggestion.votingOpenedAt) ||
      toMillis(suggestion.updatedAt) ||
      toMillis(suggestion.createdAt)
    );
  }

  return toMillis(suggestion.updatedAt) || toMillis(suggestion.createdAt);
};

export const compareSuggestionsForHost = (left: Suggestion, right: Suggestion): number => {
  const priorityDiff = getOperationalPriority(left) - getOperationalPriority(right);
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  const timeDiff = getPrimarySortMillis(right) - getPrimarySortMillis(left);
  if (timeDiff !== 0) {
    return timeDiff;
  }

  const leftId = left.id || left.suggestionId || left.uid || '';
  const rightId = right.id || right.suggestionId || right.uid || '';
  return leftId.localeCompare(rightId);
};

export const sortSuggestionsForHost = (suggestions: Suggestion[]): Suggestion[] =>
  [...suggestions].sort(compareSuggestionsForHost);
